import { useState, useMemo, useEffect } from "react";

import { IInputController } from "./types";
import {
  applyMaskToCurrencyFromDigits,
  applyMaskToCurrencyValue,
  applyMaskToValue,
  capDigitsForMask,
  getMaskedAndUnmaskedValues,
  removeMaskToCurrencyValue,
  stripDigits
} from "../../../utils/mask.utils";
import { bigInputProps } from "./styles";

const plainCurrencyValueRegex = /^\d+(\.\d+)?$/;

const formatCurrencyInputValue = (value?: string) => {
  if (!value) return applyMaskToCurrencyValue("0");

  if (plainCurrencyValueRegex.test(value)) {
    return applyMaskToCurrencyValue(value);
  }

  const unformatted = removeMaskToCurrencyValue(value);
  return applyMaskToCurrencyValue(unformatted.toString());
};

export const useInputController = ({
  mask,
  onChange,
  onClear,
  value,
  isCurrency,
  isBigInput,
  useMaskValue,
  transformValue
}: IInputController) => {
  const [showPassword, setShowPassword] = useState(false);
  const [inputValue, setInputValue] = useState<string | undefined>();

  const toogleShowPassword = () => {
    setShowPassword((prev) => !prev);
  };

  const handleOnChangeInput = (text: string) => {
    let mValue = text;
    let maskedValue: string | undefined;

    if (mask) {
      /**
       * When using a controlled masked input, some devices emit `onChangeText`
       * updates that only remove formatting characters (e.g. ")" or space),
       * keeping the same digits. Re-applying the mask would then "restore" the
       * formatting and the user gets stuck (e.g. can't delete past "(15)").
       *
       * On Android, the field may also emit digits-only text (e.g. "881332")
       * while the UI still shows the masked value ("88133-2"). That must not be
       * treated as a formatting-char delete, or digits get erased while typing.
       */
      const previousDigits = stripDigits(String(inputValue ?? ""));
      const previousMasked = applyMaskToValue(previousDigits, mask);

      let digitsOnly = capDigitsForMask(stripDigits(text), mask);

      const isDigitsOnlyText = text.length === digitsOnly.length;

      if (
        digitsOnly === previousDigits &&
        text.length < previousMasked.length &&
        digitsOnly.length > 0 &&
        !isDigitsOnlyText
      ) {
        digitsOnly = digitsOnly.slice(0, -1);
      }

      const newValues = getMaskedAndUnmaskedValues(digitsOnly, mask);
      if (useMaskValue) {
        mValue = newValues.masked;
      } else {
        mValue = newValues.unmasked;
      }
      maskedValue = newValues.masked;
    }

    if (isCurrency) {
      const previousMasked = formatCurrencyInputValue(inputValue);
      const previousDigits = stripDigits(previousMasked);
      let digitsOnly = stripDigits(text);
      const isDigitsOnlyText = text.length === digitsOnly.length;

      if (
        digitsOnly === previousDigits &&
        text.length < previousMasked.length &&
        digitsOnly.length > 0 &&
        !isDigitsOnlyText
      ) {
        digitsOnly = digitsOnly.slice(0, -1);
      }

      const centsValue = Number(digitsOnly || "0") / 100;
      mValue = centsValue.toFixed(2);
      maskedValue = applyMaskToCurrencyFromDigits(digitsOnly || "0");
    }

    if (transformValue && !mask && !isCurrency) {
      mValue = transformValue(mValue);
    }

    onChange?.(mValue, maskedValue);
    setInputValue(mValue);
  };

  const handleClear = () => {
    onClear?.();
    setInputValue("");
    onChange?.("", "");
  };

  const parsedInputValue = useMemo(() => {
    if (inputValue === null) return "";

    if (mask) {
      const digits = capDigitsForMask(stripDigits(String(inputValue ?? "")), mask);
      return applyMaskToValue(digits, mask);
    }

    if (isCurrency) {
      return formatCurrencyInputValue(inputValue);
    }
    return inputValue;
  }, [inputValue, mask, isCurrency]);

  const bigInputStyles = isBigInput ? bigInputProps : undefined;

  useEffect(() => {
    if (mask && !useMaskValue) {
      setInputValue(capDigitsForMask(stripDigits(String(value ?? "")), mask));
      return;
    }

    setInputValue(value);
  }, [value, mask, useMaskValue]);

  return {
    toogleShowPassword,
    showPassword,
    handleClear,
    parsedInputValue,
    handleOnChangeInput,
    bigInputStyles
  };
};
